import { useRef, useMemo, useState, useEffect } from 'react';
import { useScroll } from 'framer-motion';
import { AppCard } from './AppCard';
import { MergedCard } from './MergedCard';
import { BenefitCard } from './BenefitCard';
import { APP_CONFIGS, CHAOS_POSITIONS, BENEFITS, ANIMATION_CONFIG } from './constants';

export const ChaosToTidySection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Framer Motion scroll tracking (replaces custom scroll listener)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ANIMATION_CONFIG.scrollOffset,
  });

  // Track benefits visibility
  const [benefitsVisible, setBenefitsVisible] = useState(false);

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (latest) => {
      setBenefitsVisible(latest > ANIMATION_CONFIG.showBenefits);
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  // Responsive card count
  const [cardCount, setCardCount] = useState(8);

  useEffect(() => {
    const updateCardCount = () => {
      const width = window.innerWidth;
      if (width < 768) setCardCount(4);
      else if (width < 1024) setCardCount(6);
      else setCardCount(8);
    };

    updateCardCount();
    window.addEventListener('resize', updateCardCount);
    return () => window.removeEventListener('resize', updateCardCount);
  }, []);

  const activeApps = useMemo(() => APP_CONFIGS.slice(0, cardCount), [cardCount]);
  const activePositions = useMemo(() => CHAOS_POSITIONS.slice(0, cardCount), [cardCount]);

  return (
    <section
      ref={containerRef}
      aria-label="Platform Comparison"
      className="relative py-24 sm:py-32 bg-background"
      style={{ minHeight: '200vh' }}
    >
      {/* Sticky container - holds everything until animation completes */}
      <div className="sticky top-4 py-4">
        {/* Section header */}
        <header className="text-center mt-8 px-4">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-foreground mb-3">
            From chaos to clarity
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Stop juggling 8+ separate tools. Manage everything in one place.
          </p>
        </header>

        {/* Animation canvas */}
        <div
          className="relative w-full h-[45vh] sm:h-[50vh]"
          role="img"
          aria-label="Animation showing multiple apps converging into Tag-Inn platform"
        >
          {/* Scattered app cards */}
          {activeApps.map((app, index) => (
            <AppCard
              key={app.id}
              app={app}
              chaosPosition={activePositions[index]}
              scrollYProgress={scrollYProgress}
              index={index}
            />
          ))}

          {/* Merged card - Tag-Inn logo */}
          <MergedCard scrollYProgress={scrollYProgress} />
        </div>

        {/* Benefits grid - appears below logo */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full -mt-16" role="list">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            {BENEFITS.map((benefit, index) => (
              <div key={index} role="listitem">
                <BenefitCard benefit={benefit} index={index} isVisible={benefitsVisible} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
